import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

/**
 * The access token, in memory only.
 *
 * THE RULE: no authentication token is ever persisted to browser storage. Not `localStorage`, not
 * `sessionStorage`, not a URL. The refresh token lives in an httpOnly cookie the browser will not
 * show to script; the access token lives here and is GONE on reload — deliberately. On reload the
 * session is restored by renewing from the cookie, not by reading anything back.
 *
 * A module-scoped ref backs the React state so non-React callers (the API client's fetch wrapper)
 * can read the current token synchronously without a re-render.
 *
 * See docs/architecture/frontend.md §3.
 */

interface AuthState {
  accessToken: string | undefined;
  isAuthenticated: boolean;
  /**
   * True until the first renewal attempt from the refresh cookie settles.
   *
   * The access token lives in memory and is gone after a reload, so on a cold load "not
   * authenticated" and "we have not looked yet" are indistinguishable without this. Redirecting
   * during that window bounces a signed-in person to sign-in on every reload.
   */
  isRestoring: boolean;
  setAccessToken: (token: string | undefined) => void;
  finishRestoring: () => void;
  clear: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

/** Read synchronously by the API client. Never written to storage. */
let currentToken: string | undefined;

export function readAccessToken(): string | undefined {
  return currentToken;
}

export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  const [token, setToken] = useState<string | undefined>(undefined);
  const [isRestoring, setIsRestoring] = useState(true);
  const tokenRef = useRef<string | undefined>(undefined);

  const setAccessToken = useCallback((next: string | undefined) => {
    tokenRef.current = next;
    currentToken = next;
    setToken(next);
  }, []);

  const clear = useCallback(() => setAccessToken(undefined), [setAccessToken]);
  const finishRestoring = useCallback(() => setIsRestoring(false), []);

  const value = useMemo<AuthState>(
    () => ({
      accessToken: token,
      isAuthenticated: token !== undefined,
      isRestoring,
      setAccessToken,
      finishRestoring,
      clear,
    }),
    [token, isRestoring, setAccessToken, finishRestoring, clear],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside an AuthProvider.");
  return context;
}
